import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import './AuthButton.css';

interface AuthButtonProps {
  variant?: 'full' | 'compact';
}

/**
 * Authentication button component
 * Shows login/logout button based on authentication state
 * Displays owner badge for authorized admin emails
 */
export function AuthButton({ variant = 'full' }: AuthButtonProps) {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user } = useAuth0();
  const ownerStatus = useQuery(api.adminAuth.isOwner);
  const isOwner = ownerStatus?.isOwner ?? false;

  if (isLoading) {
    return (
      <div className="auth-button auth-button--loading">
        <span className="auth-button__spinner" />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className={`auth-button auth-button--authenticated ${isOwner ? 'auth-button--god-mode' : ''}`}>
        {variant === 'full' && (
          <div className="auth-button__user">
            {user.picture && (
              <img 
                src={user.picture} 
                alt={user.name || 'User'} 
                className="auth-button__avatar"
              />
            )}
            <span className="auth-button__name">{user.name || user.email}</span>
            {isOwner && (
              <span className="auth-button__god-mode-badge" title="Owner - Full Admin Access">
                ⚡
              </span>
            )}
          </div>
        )}
        <button
          onClick={() => logout({ 
            logoutParams: { 
              returnTo: window.location.origin 
            }
          })}
          className="auth-button__btn auth-button__btn--logout"
        >
          🚪 Odjava
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => loginWithRedirect()}
      className="auth-button__btn auth-button__btn--login"
    >
      🔐 Prijava
    </button>
  );
}

/**
 * User profile dropdown component
 */
export function UserProfile() {
  const { isAuthenticated, user, logout } = useAuth0();
  const [isOpen, setIsOpen] = React.useState(false);

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="user-profile">
      <button 
        className="user-profile__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {user.picture ? (
          <img 
            src={user.picture} 
            alt={user.name || 'User'} 
            className="user-profile__avatar"
          />
        ) : (
          <span className="user-profile__avatar user-profile__avatar--placeholder">
            {(user.name || user.email || 'U')[0].toUpperCase()}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="user-profile__dropdown">
          <div className="user-profile__info">
            <p className="user-profile__name">{user.name}</p>
            <p className="user-profile__email">{user.email}</p>
          </div>
          <hr className="user-profile__divider" />
          <button
            onClick={() => logout({ 
              logoutParams: { 
                returnTo: window.location.origin 
              }
            })}
            className="user-profile__logout"
          >
            🚪 Odjava
          </button>
        </div>
      )}
    </div>
  );
}
